class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]')?.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    if (this.classList.contains('header__search')) {
      this.onDoofinderCloseEvent = this.onDoofinderClose.bind(this);
      document.addEventListener('click', this.onDoofinderCloseEvent, true);
    }
  }

  onDoofinderClose(event) {
    if (!this.isOpen()) return;
    if (event.target?.closest?.('[dfd-click="close-layer"], .dfd-close-button')) this.close();
  }

  isDoofinderLayerOpen() {
    const layer = document.querySelector('.dfd-root');
    if (!layer) return false;

    const { width, height } = layer.getBoundingClientRect();
    return width > 0 && height > 0 && getComputedStyle(layer).visibility !== 'hidden';
  }

  // Doofinder is bound to the theme search input, so its layer only shows up
  // once this modal is open.
  onSearchOpen() {
    this.isMobileSearch = window.matchMedia(DetailsModal.mobileQuery).matches;

    if (this.isMobileSearch) {
      this.releaseDoofinderLayer();
      // On mobile the layer replaces the theme form, which is only kept around
      // as the trigger Doofinder listens to.
      this.doofinderInput = this.detailsContainer.querySelector('input[type="search"]');
      if (!this.doofinderInput) return;

      this.classList.add('search-modal--dfd-pending');
      this.doofinderInput.focus();
      this.doofinderInput.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }

    this.pollDoofinder(Date.now());
  }

  pollDoofinder(startedAt) {
    const layerOpen = this.isDoofinderLayerOpen();

    if (this.isMobileSearch) this.syncMobileLayer(layerOpen);
    else if (layerOpen) this.offsetDoofinderLayer();

    // Fall back to the theme form if the layer never shows up.
    if (!layerOpen && Date.now() - startedAt > DetailsModal.doofinderTimeout) {
      this.classList.remove('search-modal--dfd-pending');
      return;
    }

    if (!this.isOpen()) return;

    this.doofinderPollTimer = setTimeout(() => this.pollDoofinder(startedAt), 200);
  }

  syncMobileLayer(layerOpen) {
    if (this.classList.contains('search-modal--dfd-active')) {
      // Covers every way out of the layer: close button, ESC, back gesture.
      if (!layerOpen) this.close();
      return;
    }

    if (!layerOpen) return;

    this.classList.remove('search-modal--dfd-pending');
    this.classList.add('search-modal--dfd-active');
    // Typing must reach the layer input, not the hidden theme one.
    if (document.activeElement === this.doofinderInput) this.doofinderInput.blur();
  }

  // Doofinder sets `position: absolute` and a `top` computed from the scroll position
  // when it opens, so with the page scrolled the layer ends up outside the viewport.
  // Inline declarations with !important are the only way to override it: we pin the
  // layer to the viewport, right below the (sticky) desktop header. The offset follows
  // the header's real bottom edge, so anything above it (notification bar...) is accounted for.
  offsetDoofinderLayer() {
    const layer = document.querySelector('.dfd-root');
    if (!layer) return;

    const header = document.querySelector('.header-wrapper');
    const top = header ? `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px` : DetailsModal.doofinderDesktopOffset;

    const { style } = layer;
    if (style.position === 'fixed' && style.top === top) return;

    style.setProperty('position', 'fixed', 'important');
    style.setProperty('top', top, 'important');
    this.doofinderLayerPinned = true;
  }

  // The layer element is reused between openings (and between breakpoints), so the
  // desktop overrides must not leak into the mobile layout.
  releaseDoofinderLayer() {
    if (!this.doofinderLayerPinned) return;
    this.doofinderLayerPinned = false;

    const layer = document.querySelector('.dfd-root');
    if (!layer) return;

    layer.style.removeProperty('position');
    layer.style.removeProperty('top');
  }

  resetDoofinder() {
    clearTimeout(this.doofinderPollTimer);
    this.releaseDoofinderLayer();
    this.classList.remove('search-modal--dfd-pending', 'search-modal--dfd-active');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (event.target?.closest && event.target.closest('.dfd-root')) return;
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    if (!this.classList.contains('header__search')) {
      document.body.classList.add('overflow-hidden');
    }

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );

    if (this.classList.contains('header__search')) this.onSearchOpen();
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    if (!this.classList.contains('header__search')) {
      document.body.classList.remove('overflow-hidden');
    } else {
      this.resetDoofinder();
    }
  }
}

DetailsModal.mobileQuery = '(max-width: 749px)';
DetailsModal.doofinderTimeout = 5000;
// Fallback when the header can't be found.
DetailsModal.doofinderDesktopOffset = '80px';

customElements.define('details-modal', DetailsModal);
